import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForexWithRefresh, useForexStore } from '@/hooks/useForex'
import {
  RefreshCw, TrendingUp, TrendingDown, DollarSign, ArrowUpRight,
  ArrowDownRight, AlertCircle, Globe, Activity, BarChart3, ChevronRight,
} from 'lucide-react'
import { format } from 'date-fns'
import { safeFormat } from '../lib/format'
import type { FxPair, ForexPairDetail } from '@/lib/types'

/* ─── Helpers ────────────────────────────────────────────────────────────── */

function fmtRate(n: number | undefined | null, pair?: string): string {
  if (n == null) return '--'
  // JPY pairs have fewer decimal places
  const isJpy = pair?.includes('JPY')
  return n.toLocaleString('en-US', {
    minimumFractionDigits: isJpy ? 2 : 4,
    maximumFractionDigits: isJpy ? 3 : 5,
  })
}

function ChangeChip({ v, pair }: { v: number | null | undefined; pair?: string }) {
  if (v == null) return <span className="text-text-muted">--</span>
  const pos = v >= 0
  return (
    <span className={pos ? 'delta-chip-pos' : 'delta-chip-neg'}>
      {pos ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
      {pos ? '+' : ''}{v.toFixed(4)}
    </span>
  )
}

function PctChip({ v }: { v: number | null | undefined }) {
  if (v == null) return <span className="text-text-muted">--</span>
  const pos = v >= 0
  return (
    <span className={pos ? 'delta-chip-pos' : 'delta-chip-neg'}>
      {pos ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
      {pos ? '+' : ''}{v.toFixed(3)}%
    </span>
  )
}

function TrendBadge({ trend }: { trend: string }) {
  const map: Record<string, string> = {
    up: 'text-emerald-400',
    down: 'text-rose-400',
    flat: 'text-text-muted',
  }
  const icon = trend === 'up' ? <TrendingUp size={12} /> : trend === 'down' ? <TrendingDown size={12} /> : <span>—</span>
  return <span className={`inline-flex items-center gap-0.5 ${map[trend] ?? 'text-text-muted'}`}>{icon}</span>
}

/* Cross-rate matrix cell */
function MatrixCell({
  value, isIdentity, isBold, onClick,
}: {
  value: number | null
  isIdentity: boolean
  isBold: boolean
  onClick?: () => void
}) {
  if (isIdentity) {
    return (
      <td className="text-center text-xs p-1.5 bg-slate-500/15 font-bold text-text-muted select-none">1.0</td>
    )
  }
  if (value == null) {
    return <td className="text-center text-xs p-1.5 text-text-muted">--</td>
  }
  return (
    <td
      className={`text-center text-xs p-1.5 cursor-pointer hover:bg-blue/20 transition-colors ${isBold ? 'font-bold text-text-primary' : 'text-text-secondary'}`}
      onClick={onClick}
      title="Click to view pair detail"
    >
      {Math.abs(value) >= 100 ? value.toFixed(2) : value.toFixed(4)}
    </td>
  )
}

/* ─── Pair Detail Panel ──────────────────────────────────────────────────── */

function PairDetailPanel({ pair, onClose }: { pair: ForexPairDetail; onClose: () => void }) {
  const navigate = useNavigate()
  const diff = pair.rate_differential
  const tech = pair.technicals

  return (
    <div className="card p-5 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-bold text-text-primary">{pair.display}</h3>
          <p className="text-text-muted text-xs">{pair.pair}</p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            className="btn-secondary text-xs gap-1.5"
            onClick={() => navigate(`/app/chart?ticker=${pair.pair}%3DX`)}
          >
            <BarChart3 size={12} aria-hidden /> Chart
          </button>
          <button type="button" onClick={onClose} className="btn-secondary text-xs">Close</button>
        </div>
      </div>

      {/* Key metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="stat-card">
          <p className="stat-label">Rate</p>
          <p className="stat-value text-lg">{fmtRate(pair.rate, pair.pair)}</p>
          <PctChip v={pair.change_pct} />
        </div>
        <div className="stat-card">
          <p className="stat-label">Day Range</p>
          <p className="stat-value">{fmtRate(pair.day_low, pair.pair)} – {fmtRate(pair.day_high, pair.pair)}</p>
        </div>
        <div className="stat-card">
          <p className="stat-label">52w High</p>
          <p className="stat-value">{fmtRate(pair.week_52_high, pair.pair)}</p>
        </div>
        <div className="stat-card">
          <p className="stat-label">52w Low</p>
          <p className="stat-value">{fmtRate(pair.week_52_low, pair.pair)}</p>
        </div>
      </div>

      {/* Rate Differential */}
      <div className="card bg-surface-card p-4 space-y-2">
        <h4 className="panel-title flex items-center gap-2">
          <Activity size={13} className="text-blue-400" aria-hidden /> Rate Differential
        </h4>
        <div className="grid grid-cols-3 gap-4 text-sm">
          <div className="text-center">
            <p className="text-text-muted text-xs">{diff.base_cb} ({diff.base_currency})</p>
            <p className="text-xl font-bold text-text-primary">{(diff.base_rate ?? 0).toFixed(2)}%</p>
          </div>
          <div className="text-center flex flex-col items-center justify-center">
            <p className={`text-lg font-bold ${diff.differential > 0 ? 'text-emerald-400' : diff.differential < 0 ? 'text-rose-400' : 'text-text-muted'}`}>
              {(diff.differential ?? 0) > 0 ? '+' : ''}{(diff.differential ?? 0).toFixed(2)}%
            </p>
            <p className="text-xs text-text-muted">differential</p>
            <span className={`badge text-xs mt-1 ${
              diff.carry_signal === 'long_base' ? 'badge-green' :
              diff.carry_signal === 'long_quote' ? 'badge-red' : 'badge-blue'
            }`}>
              {diff.carry_signal.replace('_', ' ')}
            </span>
          </div>
          <div className="text-center">
            <p className="text-text-muted text-xs">{diff.quote_cb} ({diff.quote_currency})</p>
            <p className="text-xl font-bold text-text-primary">{(diff.quote_rate ?? 0).toFixed(2)}%</p>
          </div>
        </div>
      </div>

      {/* Technical Levels */}
      <div className="card bg-surface-card p-4">
        <h4 className="panel-title mb-3">Pivot Levels</h4>
        <div className="grid grid-cols-4 gap-2 text-xs">
          {[
            { label: 'R3', val: tech.r3, color: 'text-emerald-300' },
            { label: 'R2', val: tech.r2, color: 'text-emerald-400' },
            { label: 'R1', val: tech.r1, color: 'text-emerald-500' },
            { label: 'Pivot', val: tech.pivot, color: 'text-blue-400' },
            { label: 'S1', val: tech.s1, color: 'text-rose-500' },
            { label: 'S2', val: tech.s2, color: 'text-rose-400' },
            { label: 'S3', val: tech.s3, color: 'text-rose-300' },
          ].map(({ label, val, color }) => (
            <div key={label} className="stat-card text-center">
              <p className={`font-semibold ${color}`}>{label}</p>
              <p className="text-text-primary mt-1">{fmtRate(val, pair.pair)}</p>
            </div>
          ))}
          <div className="stat-card text-center">
            <p className="text-text-muted">Trend</p>
            <div className="mt-1 flex justify-center"><TrendBadge trend={pair.trend} /></div>
          </div>
        </div>
      </div>
    </div>
  )
}

/* ─── Main Page ──────────────────────────────────────────────────────────── */

export default function ForexPage() {
  const { dashboard, crossRates, loading, refreshing, error } = useForexWithRefresh(30_000)
  const { fetchPairDetail, pairDetail, pairLoading, fetchAll } = useForexStore()
  const [selectedPair, setSelectedPair] = useState<string | null>(null)
  const navigate = useNavigate()

  const handlePairClick = async (pair: string) => {
    setSelectedPair(pair)
    await fetchPairDetail(pair)
  }

  const handleMatrixClick = (base: string, quote: string) => {
    if (base === quote) return
    const pair = `${base}${quote}`
    handlePairClick(pair)
  }

  const dxy = dashboard?.dxy
  const pairs = dashboard?.pairs ?? []
  const topMovers = dashboard?.top_movers ?? []
  const gainers = dashboard?.gainers ?? []
  const losers = dashboard?.losers ?? []
  const cbRates = dashboard?.cb_rates ?? {}
  const cbNames = dashboard?.cb_names ?? {}
  const currencies = crossRates?.currencies ?? dashboard?.currencies ?? []
  const matrix = crossRates?.matrix ?? []

  // Group pairs for the 7-pair major display (USD pairs)
  const majorPairs = pairs.filter(p => p.base === 'USD' || p.quote === 'USD')

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="page-title flex items-center gap-2">
            <DollarSign size={20} className="text-emerald-400" aria-hidden />
            Forex Dashboard
          </h1>
          <div className="flex items-center gap-2">
            {dashboard && (
              <>
                <p className="text-sm text-text-secondary">
                  Updated {safeFormat(dashboard.last_updated, 'HH:mm', format)}
                </p>
                <span className="chip-live">live</span>
              </>
            )}
            {refreshing && (
              <span className="text-xs text-text-muted flex items-center gap-1">
                <RefreshCw size={11} className="animate-spin" aria-hidden />
                refreshing...
              </span>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={() => fetchAll(true)}
          className="btn-secondary gap-1.5 text-xs"
          disabled={loading || refreshing}
        >
          <RefreshCw size={13} className={loading || refreshing ? 'animate-spin' : ''} aria-hidden />
          Refresh
        </button>
      </div>

      {error && (
        <div className="card p-4 border border-rose-500/30 bg-rose-500/5 flex items-center gap-2 text-sm">
          <AlertCircle size={15} className="text-rose-400 shrink-0" aria-hidden />
          <span className="text-rose-400">{error}</span>
          {dashboard && <span className="ml-auto text-text-muted text-xs">Showing last known data</span>}
        </div>
      )}

      {/* ── DXY + CB Rates ───────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 stagger-children">
        {/* DXY Hero Card */}
        <div className="stat-card md:col-span-2 border border-blue/20 bg-blue/5">
          <div className="flex items-center gap-2 mb-1">
            <DollarSign size={14} className="text-blue-400" aria-hidden />
            <p className="stat-label">US Dollar Index (DXY)</p>
          </div>
          <p className="stat-hero text-gradient">
            {dxy ? dxy.level.toFixed(3) : '--'}
          </p>
          {dxy && <PctChip v={dxy.change_pct} />}
          {dxy && (
            <p className={`text-xs mt-1 ${dxy.change >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
              {(dxy.change ?? 0) >= 0 ? '+' : ''}{(dxy.change ?? 0).toFixed(3)} today
            </p>
          )}
        </div>

        {/* CB Rates */}
        {Object.entries(cbRates).slice(0, 6).map(([ccy, rate]) => (
          <div key={ccy} className="stat-card">
            <p className="stat-label">{cbNames[ccy] ?? ccy} ({ccy})</p>
            <p className="stat-value text-lg">{rate.toFixed(2)}%</p>
            <p className="text-xs text-text-muted">policy rate</p>
          </div>
        ))}
      </div>

      {/* ── Pair Detail (if selected) ────────────────────────────────────── */}
      {selectedPair && pairDetail && !pairLoading && (
        <PairDetailPanel pair={pairDetail} onClose={() => setSelectedPair(null)} />
      )}
      {selectedPair && pairLoading && (
        <div className="card p-8 text-center text-text-muted text-sm">Loading pair data...</div>
      )}

      {/* ── Major Pairs Table ─────────────────────────────────────────────── */}
      <div className="card overflow-hidden">
        <div className="px-5 py-4 border-b border-border flex items-center gap-2">
          <Globe size={15} className="text-text-muted" aria-hidden />
          <h2 className="panel-title">Major Pairs</h2>
          <span className="text-xs text-text-muted ml-auto">{pairs.length} pairs tracked</span>
        </div>
        <div className="overflow-x-auto">
          <table className="table-pro w-full text-sm">
            <thead>
              <tr>
                <th>Pair</th>
                <th className="!text-right">Rate</th>
                <th className="!text-right">Change</th>
                <th className="!text-right">Change %</th>
                <th className="!text-center">Trend</th>
                <th className="!text-right">Detail</th>
              </tr>
            </thead>
            <tbody>
              {pairs.map(p => (
                <tr
                  key={p.pair}
                  className="cursor-pointer"
                  onClick={() => handlePairClick(p.pair)}
                >
                  <td className="font-medium text-text-primary">{p.display}</td>
                  <td className="text-right tabular-nums text-text-primary">
                    {fmtRate(p.rate, p.pair)}
                  </td>
                  <td className="text-right">
                    <ChangeChip v={p.change} pair={p.pair} />
                  </td>
                  <td className="text-right">
                    <PctChip v={p.change_pct} />
                  </td>
                  <td className="text-center">
                    <TrendBadge trend={p.trend} />
                  </td>
                  <td className="text-right">
                    <ChevronRight size={13} className="text-text-muted inline" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!loading && pairs.length === 0 && (
          <p className="p-8 text-center text-text-muted text-sm">No FX data available.</p>
        )}
      </div>

      {/* ── Top Movers ───────────────────────────────────────────────────── */}
      {topMovers.length > 0 && (
        <div className="grid md:grid-cols-2 gap-4">
          <div className="card overflow-hidden">
            <div className="px-5 py-4 border-b border-border flex items-center gap-2">
              <TrendingUp size={15} className="text-emerald-400" aria-hidden />
              <h2 className="panel-title">Biggest Movers (Today)</h2>
            </div>
            <ul className="divide-y divide-border">
              {topMovers.slice(0, 6).map(p => (
                <li
                  key={p.pair}
                  className="px-5 py-3 flex items-center justify-between hover:bg-surface-card cursor-pointer"
                  onClick={() => handlePairClick(p.pair)}
                >
                  <span className="font-medium text-text-primary text-sm">{p.display}</span>
                  <div className="text-right">
                    <span className="text-text-primary text-sm tabular-nums">{fmtRate(p.rate, p.pair)}</span>
                    <div><PctChip v={p.change_pct} /></div>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          {/* Gainers / Losers */}
          <div className="card overflow-hidden">
            <div className="px-5 py-4 border-b border-border flex items-center gap-2">
              <Activity size={15} className="text-text-muted" aria-hidden />
              <h2 className="panel-title">Gainers &amp; Losers</h2>
            </div>
            <div className="divide-y divide-border">
              {gainers.slice(0, 3).map(p => (
                <div key={p.pair} className="px-5 py-2.5 flex items-center justify-between hover:bg-surface-card cursor-pointer"
                  onClick={() => handlePairClick(p.pair)}>
                  <span className="text-sm text-text-primary">{p.display}</span>
                  <PctChip v={p.change_pct} />
                </div>
              ))}
              <div className="border-t border-border/50" />
              {losers.slice(0, 3).map(p => (
                <div key={p.pair} className="px-5 py-2.5 flex items-center justify-between hover:bg-surface-card cursor-pointer"
                  onClick={() => handlePairClick(p.pair)}>
                  <span className="text-sm text-text-primary">{p.display}</span>
                  <PctChip v={p.change_pct} />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── Cross-Rate Matrix ─────────────────────────────────────────────── */}
      {matrix.length > 0 && currencies.length > 0 && (
        <div className="card overflow-hidden">
          <div className="px-5 py-4 border-b border-border flex items-center gap-2">
            <Globe size={15} className="text-text-muted" aria-hidden />
            <h2 className="panel-title">Cross-Rate Matrix</h2>
            <span className="text-xs text-text-muted ml-2">Click any cell to view pair detail</span>
          </div>
          <div className="overflow-x-auto p-4">
            <table className="text-xs">
              <thead>
                <tr>
                  <th className="p-2 text-text-muted font-medium w-12"></th>
                  {currencies.map(c => (
                    <th key={c} className="p-2 text-text-muted font-medium text-center min-w-[72px]">{c}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {currencies.map((base, i) => (
                  <tr key={base}>
                    <td className="p-2 text-text-muted font-medium pr-4">{base}</td>
                    {currencies.map((quote, j) => (
                      <MatrixCell
                        key={quote}
                        value={matrix[i]?.[j] ?? null}
                        isIdentity={i === j}
                        isBold={quote === 'USD' || base === 'USD'}
                        onClick={() => handleMatrixClick(base, quote)}
                      />
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-text-muted px-5 pb-3">
            Rows = base currency. Columns = quote currency. Rate = units of column per 1 unit of row.
          </p>
        </div>
      )}
    </div>
  )
}
